import { ActionFunction, redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Types
import { AuthContextType } from '../../../hooks/useAuth';
import { AppConfigContextType } from '../../../hooks/useAppConfig';

// GraphQL Client
import { cacheApolloPersistor, client } from '../../../gql/client';
import { purgeSeedForCurrentUser } from '../../../gql/cacheSeed';
import { clearRoadMeshes } from '../../../offline/roadMeshStore';

import { DELETE_ACTION_BY_ID } from '../../../gql/mutations/actions';
import { GET_ORDER_BY_ID } from '../../../gql/queries/orders/orders';
// Types and Const
import { Store } from 'redux';
import { RootState } from '../../../state/configureStore';
import { formActionNaming as actionFormNaming } from '../../forms/ActionForms/constants';

import { OrderState } from '../../../types/orders';
// Action
import { cleanState } from '../../../state/actions/main';
import { persistor } from '../../../state/configureStore';

import { getInitialStatePersist } from '../../../state/selectors/persist';
// Utils
import { decompressSessionStorage } from '../../../utils/sessionStorage';
import { clearNativeStorage } from '../../../offline/nativeDurableStorage';
import { purgeUserStorage } from '../../../utils/purgeUserStorage';
import { sealUnownedQueuedWork } from '../../../offline/sealUnownedQueuedWork';

/** Ejecuta un paso del logout sin que su fallo pueda dejar la sesión abierta. */
const step = async (name: string, run: () => Promise<unknown> | unknown) => {
  try {
    await run();
  } catch (e) {
    Sentry.captureException(e, { tags: { feature: 'logout', step: name } });
  }
};

export const ActionLogout: (
  store: Store<RootState>
) => (
  userData: AuthContextType | null,
  appConfigContext: AppConfigContextType | null
) => ActionFunction | undefined =
  (store) => (userData, appConfigContext) => async () => {
    // 🔴 TODO ESTE BLOQUE VA EN try/catch, y el motivo importa.
    //
    // Antes, el `client.query` de abajo quedaba FUERA de cualquier try. Sin
    // cobertura la promesa rechaza, el ActionFunction entero se aborta y NADA
    // de la limpieza de más abajo llega a ejecutarse: ni Apollo, ni Redux, ni
    // los formularios, ni las colas offline, ni `clearNativeSession()`.
    //
    // En web era una molestia: cerrar la pestaña se llevaba el rastro. En la
    // app nativa, con la sesión espejada a `localStorage`, significa que el
    // operario cree que ha cerrado sesión, la app se queda con su usuario
    // guardado y al reabrir entra directo. Y el escenario que lo dispara —sin
    // cobertura— es precisamente el de campo.
    //
    // Borrar la actuación parcial es una cortesía; cerrar la sesión es el
    // contrato. Si lo primero falla, lo segundo sigue adelante.
    try {
    const actionData = sessionStorage.getItem(actionFormNaming.NEW_ACTION_DATA);
    if (actionData) {
      const dataParsed = JSON.parse(decompressSessionStorage(actionData));
      if (dataParsed.orderId) {
        const {
          data: {
            order: { state },
          },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: dataParsed.orderId,
          },
          fetchPolicy: 'network-only',
        });
        try {
          state === OrderState.Partial &&
            (await client.mutate({
              mutation: DELETE_ACTION_BY_ID,
              variables: {
                id: dataParsed.actionId,
              },
            }));
        } catch (e) {
          Sentry.captureException(e);
          console.log(e);
        }
      }
    }
    } catch (e) {
      // Sin red, con una sesión ilegible o con una OT ya borrada: se registra
      // y se sigue cerrando sesión.
      Sentry.captureException(e);
    }

    // 🔴 Cada paso de aquí abajo va por `step()`: su fallo se registra y el
    // cierre sigue. Antes `persistor.purge()`, Apollo y `clearRoadMeshes()` iban
    // sin `try` ANTES de `setUser(null)`, así que un IndexedDB bloqueado cortaba
    // el logout con la sesión abierta. Cerrar la sesión es el contrato.

    //TODO: apollo revoke refresh token with black list tokens and remove of database with expire refresh token or other form, that i dont know, now
    await step('redux', async () => {
      store.dispatch(cleanState(getInitialStatePersist(store.getState())));
      await persistor.purge();
    });
    await step('apollo', async () => {
      // `FreshnessCache` conserva su interruptor tras `reset()`: no hay nada que
      // volver a aplicar (spec 2026-10-01 § 7, Fase 3b).
      await client.clearStore();
      await client.cache.reset({ discardWatches: true });
      await cacheApolloPersistor.purge();
    });

    // ── Lo que depende de SABER QUIÉN SALE: antes de `setUser(null)` ────────
    // La semilla de arranque en frío (`tenantId::userId`), la malla de PK
    // (indexada por la misma identidad) y los borradores nativos del APK
    // (namespace derivado de la sesión) se localizan con la sesión viva.
    // Después de limpiarla ya no se sabría cuáles borrar.
    //
    // `clearNativeStorage()` iba DESPUÉS de `setUser(null)` y por eso no
    // borraba los borradores del operario que salía —su propio comentario
    // pedía lo contrario—. Ahora va aquí. Además borra el espejo persistente
    // de la sesión en Preferences, que sin esto sobreviviría al cierre.
    await step('seed', () => purgeSeedForCurrentUser());
    await step('roadMesh', () => clearRoadMeshes());
    await step('native', () => clearNativeStorage());
    // Las colas ya NO se borran al salir: son trabajo sin enviar y cada
    // operación solo la envía su dueño (#331). Lo encolado antes de la #331 no
    // tiene dueño y lo enviaría cualquiera: se sella aquí con el que sale, que
    // aún se sabe quién es (spec 2026-10-12).
    await step('sealQueues', () => sealUnownedQueuedWork());

    userData?.setUser(null);
    Sentry.setUser(null);
    appConfigContext?.clearAppConfig();

    // ── Todo lo demás del usuario, por barrido ──────────────────────────────
    // Avisos y rastro GPS ya subido (IndexedDB), OPFS salvo las fotos
    // pendientes, `sessionStorage` salvo marcas técnicas y el tamaño de página
    // por tabla. El trabajo sin enviar se queda (spec 2026-10-12).
    // Sustituye a las listas a mano que había aquí y que se quedaron viejas
    // (sobrevivían las fotos de la bandeja, los avisos de fotos perdidas y
    // ~15 familias de `sessionStorage`). Nunca lanza.
    //
    // 🔴 Lo que ya NO se hace: vaciar CacheStorage. Solo contiene la precaché
    // de la app (no hay `runtimeCaching`; el comentario que hablaba de
    // respuestas `/graphql` cacheadas describía algo que no existe), y Workbox
    // no la repara sin `integrity`: la web se quedaba sin arranque offline
    // hasta el siguiente despliegue (medido 2026-10-08). Detalle en
    // `utils/purgeUserStorage.ts`.
    await purgeUserStorage();

    return redirect('/login');
  };
